import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  Create,
  SimpleForm,
  ImageInput,
  TextInput,
  NumberInput,
  ImageField,
  Toolbar,
  SaveButton,
  required,
  minValue,
  SelectArrayInput,
  SelectInput,
  useNotify,
  useRedirect,
} from 'react-admin';
import dataProvider from '../../providers/dataProvider';
import { paymentTypeSelectOptions } from '../../constants/paymentType';
import uploadPicture from '../../utils/uploadPicture';
import ImagePickerButton from '../../components/ImagePickerButton/ImagePickerButton';
import removeUnnecessaryProductFields from '../../utils/removeUnnecessaryProductFields';
import Loading from '../../components/Loading/Loading';
import PaymentTypeAdditionalField from './components/PaymentTypeAdditionalField';

const GET_MANY = 'GET_MANY';
const CREATE = 'CREATE';

const saveProduct = (values) => {
  return dataProvider(CREATE, 'admin/products', { data: values });
};

const ProductCreateToolbar = ({ loading, ...props }) => {
  return (
    <>
      <Loading open={loading} />
      <Toolbar {...props}>
        <SaveButton />
      </Toolbar>
    </>
  );
};

const CreateProduct = (props) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const [options, changeOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dataProvider(GET_MANY, 'admin/products/programs').then((res) => changeOptions(res.data));
  }, []);

  const handleSubmit = useCallback(
    async (values) => {
      setLoading(true);
      try {
        const res = await uploadPicture(values.picture);
        if (!res?.data?.fileName) {
          throw new Error('Failed to upload image');
        }
        const newData = removeUnnecessaryProductFields({ ...values, picture: res.data.fileName });
        await saveProduct(newData);
        notify('Product created successfully', { type: 'success' });
        redirect('list', 'admin/products');
      } catch (error) {
        notify(error?.message || 'Failed to create product', { type: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [notify, redirect],
  );

  return (
    <Create {...props}>
      <SimpleForm onSubmit={handleSubmit} toolbar={<ProductCreateToolbar loading={loading} />}>
        <TextInput source="name" label="Product Name" validate={required()} />
        <TextInput source="link" label="Checkout Link" />
        <TextInput source="cancelUrl" label="Cancel URL" />
        <TextInput source="externalProductId" label="External Product Id" />
        <SelectInput source="paymentType" choices={paymentTypeSelectOptions} defaultValue={0} />
        <NumberInput
          source="price"
          label="Price, $"
          validate={[required(), minValue(0)]}
          defaultValue={0}
        />
        <PaymentTypeAdditionalField />
        <TextInput
          source="stripePriceId"
          label="Stripe Price Id"
          helperText={
            <span>
              It&apos;s required for stripe products
              <br />
              if isPaymentPlan selected, this should be the subscription stripe price id
            </span>
          }
        />
        <NumberInput source="upsellPrice" label="Upsell Price, $" />
        <TextInput source="upsellStripePriceId" label="Upsell Stripe Price Id" />
        <TextInput
          source="mnPlanId"
          label="Mighty Networks Plan ID"
          helperText="If set, purchasing this product will grant access to this MN plan"
        />
        {options.length ? (
          <SelectArrayInput source="programs" choices={options} validate={required()} />
        ) : null}
        <TextInput source="description" label="Description" validate={required()} />
        <ImageInput
          source="picture"
          label="Add Product Photo"
          accept={{ 'image/*': [] }}
          validate={required()}
        >
          <ImageField source="src" title="title" />
        </ImageInput>
        <ImagePickerButton source="picture" label={false} />
      </SimpleForm>
    </Create>
  );
};

export default memo(CreateProduct);
